import React, { useState } from 'react';
import { Box } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import DownloadIcon from '@mui/icons-material/Download';
import VideocamOffIcon from '@mui/icons-material/VideocamOff';
import NavBar from '../components/navbar';
import Footer from '../components/footer';
import PageHero from '../components/PageHero';
import SectionHeader from '../components/SectionHeader';
import CtaSection from '../components/CtaSection';
import ScrollReveal from '../components/animated/ScrollReveal';
import MagneticButton from '../components/animated/MagneticButton';
import Text from '../components/Text';
import ErmScorecard from '../components/erm/ErmScorecard';
import ermFramework from '../assets/images/erm-framework-hd.png';
import heroErm from '../assets/images/hero-erm.png';
import { gradients, colors, containImageSx } from '../theme/designTokens';
import { useHashScroll } from '../hooks/useHashScroll';

const INDEX_PILLARS = [
  {
    number: '01',
    title: 'Risk Identification',
    description:
      'Systematic mapping of operational, financial, strategic and insurable risks across every point in your value chain.',
    score: 92,
  },
  {
    number: '02',
    title: 'Coverage Adequacy',
    description:
      'Evaluation of protection terms, limits and exclusions against actual exposures to eliminate gaps and overlaps.',
    score: 88,
  },
  {
    number: '03',
    title: 'Cost of Risk Efficiency',
    description:
      'Benchmarking risk finance, retention levels and transfer strategies to optimize your total cost of risk.',
    score: 85,
  },
  {
    number: '04',
    title: 'Claims Performance',
    description:
      'Measuring settlement speed, recovery ratios and outcomes as indicators of program effectiveness.',
    score: 90,
  },
  {
    number: '05',
    title: 'Response Readiness',
    description:
      'Assessing business continuity plans, disaster recovery frameworks and incident response capabilities.',
    score: 87,
  },
  {
    number: '06',
    title: 'Governance & Integration',
    description:
      'Reviewing how risk management is embedded in decision-making, reporting and cross-functional accountability.',
    score: 84,
  },
];

const METHODOLOGY_STEPS = [
  {
    number: '01',
    title: 'Baseline assessment',
    description:
      "We apply ALEgION's proprietary all-risk-point template to catalogue exposures across your operations, supply chain and balance sheet.",
  },
  {
    number: '02',
    title: 'Index scoring',
    description:
      'Each pillar is scored on a standardized scale using quantitative data, protection analysis and qualitative risk engineering inputs.',
  },
  {
    number: '03',
    title: 'Gap analysis',
    description:
      'Low-scoring areas are flagged with actionable recommendations—from protection architecture to process improvements.',
  },
  {
    number: '04',
    title: 'Continuous improvement',
    description:
      'The index is tracked over time so you can measure progress, justify investments and demonstrate risk maturity to stakeholders.',
  },
];

const BENEFITS = [
  'Single, comparable metric for enterprise-wide risk management performance',
  'Identifies coverage gaps before they become costly claims',
  'Supports board-level reporting with clear, data-driven insights',
  'Quantifies where resilience investment will have the greatest impact',
  'Aligns with AERA™ Assess and Advance layers for continuous maturity',
  'Built on methodologies pioneered by ALEgION in enterprise risk management',
];

const ErmIndex = () => {
  const navigate = useNavigate();
  const [videoError, setVideoError] = useState(false);
  useHashScroll();

  return (
    <Box sx={{ bgcolor: colors.white, overflow: 'hidden' }}>
      <NavBar />

      <PageHero
        variant="dark"
        eyebrow="Enterprise Risk Management"
        title="ERM Efficiency"
        titleHighlight="Index"
        subtitle="A proprietary framework from ALEgION to measure, benchmark and improve how effectively your organization manages risk across every point of exposure—feeding AERA™ with measurable maturity."
        badges={['All-Risk-Point', 'Proprietary Index', 'Board-Ready']}
      />

      {/* Overview */}
      <Box sx={{ px: { xs: 2, sm: 3, md: 4 }, py: { xs: 8, md: 12 }, background: colors.offWhite }}>
        <Box
          sx={{
            maxWidth: 1100,
            mx: 'auto',
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', lg: '1fr 1fr' },
            gap: { xs: 4, lg: 6 },
            alignItems: 'center',
          }}
        >
          <ScrollReveal>
            <Box
              sx={{
                p: { xs: 2, md: 3 },
                background: colors.white,
                border: `1px solid ${colors.borderLight}`,
              }}
            >
              <Box
                component="img"
                src={ermFramework}
                alt="Enterprise Risk Management framework"
                sx={{ ...containImageSx, width: '100%', height: 'auto' }}
              />
            </Box>
          </ScrollReveal>

          <ScrollReveal delay={0.1}>
            <Text variant="eyebrow" sx={{ color: colors.slate, mb: 2, display: 'block' }}>
              The Framework
            </Text>
            <Text variant="h2" color="primary" sx={{ mb: 2 }}>
              Measure what resilience requires.
            </Text>
            <Text variant="body" color="secondary" sx={{ mb: 2, display: 'block' }}>
              The ERM Efficiency Index extends ALEgION&apos;s all-risk-point approach into a measurable index—so boards and operators can see strengths, gaps, and where to invest next.
            </Text>
            <Text variant="body" color="secondary" sx={{ mb: 3, display: 'block' }}>
              It sits inside AERA™ as a core Assess and Advance capability: diagnostics that become continuous improvement.
            </Text>
            <MagneticButton variant="navy" onClick={() => navigate('/framework')}>
              See AERA™
            </MagneticButton>
          </ScrollReveal>
        </Box>
      </Box>

      {/* Video */}
      <Box sx={{ px: { xs: 2, sm: 3, md: 4 }, py: { xs: 8, md: 12 }, background: gradients.sectionDark }}>
        <Box sx={{ maxWidth: 1200, mx: 'auto' }}>
          <ScrollReveal>
            <SectionHeader
              eyebrow="Walkthrough"
              title="See the methodology."
              highlightWords={['methodology']}
              subtitle="A short overview of ALEgION's all-risk-point approach and how each pillar informs your ERM Efficiency Index."
              light
            />
          </ScrollReveal>
          <Box sx={{ textAlign: 'center', mb: 4 }}>
            <MagneticButton
              variant="gold"
              component="a"
              href="https://erm.alegion.net/"
              target="_blank"
              rel="noopener noreferrer"
            >
              Try ERM Efficiency Index
            </MagneticButton>
          </Box>
          <ScrollReveal delay={0.1}>
            <Box
              sx={{
                position: 'relative',
                width: '100%',
                aspectRatio: '16 / 9',
                border: `1px solid ${colors.glassBorder}`,
                overflow: 'hidden',
                background: colors.navyDark,
                boxShadow: '0 24px 64px rgba(0,0,0,0.45)',
              }}
            >
              {!videoError ? (
                <Box
                  component="video"
                  src="/videos/ERM_Overview.mp4"
                  poster={heroErm}
                  controls
                  playsInline
                  preload="metadata"
                  controlsList="nodownload"
                  onError={() => setVideoError(true)}
                  sx={{
                    width: '100%',
                    height: '100%',
                    display: 'block',
                    objectFit: 'cover',
                    bgcolor: '#000',
                  }}
                >
                  Your browser does not support the video tag.
                </Box>
              ) : (
                <Box
                  sx={{
                    position: 'absolute',
                    inset: 0,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 1.5,
                    px: 3,
                    textAlign: 'center',
                  }}
                >
                  <VideocamOffIcon sx={{ fontSize: 48, color: colors.silver }} />
                  <Text variant="h4" color="white">
                    Video unavailable
                  </Text>
                  <Text variant="bodySm" color="muted">
                    The walkthrough could not be loaded. Download the briefing deck below or contact us for a live demo.
                  </Text>
                </Box>
              )}
            </Box>
          </ScrollReveal>
        </Box>
      </Box>

      {/* Download */}
      <Box sx={{ px: { xs: 2, sm: 3, md: 4 }, py: { xs: 8, md: 10 }, background: colors.white }}>
        <Box
          sx={{
            maxWidth: 800,
            mx: 'auto',
            p: { xs: 3, md: 5 },
            border: `1px solid ${colors.borderLight}`,
            textAlign: 'center',
          }}
        >
          <ScrollReveal>
            <Text variant="eyebrow" sx={{ color: colors.slate, mb: 2, display: 'block' }}>
              Briefing Deck
            </Text>
            <Text variant="h3" color="primary" sx={{ mb: 2 }}>
              Download the ERM Efficiency Index overview
            </Text>
            <Text variant="body" color="secondary" sx={{ mb: 3, display: 'block' }}>
              Take the full framework briefing offline—precision enterprise risk methodology from ALEgION.
            </Text>
            <MagneticButton
              variant="navy"
              component="a"
              href="/downloads/Precision_Enterprise_Risk.pptx"
              download="ALEgION_ERM_Efficiency_Index.pptx"
            >
              <DownloadIcon sx={{ mr: 1, fontSize: 18 }} />
              Download PPTX
            </MagneticButton>
          </ScrollReveal>
        </Box>
      </Box>

      <Box id="erm-scorecard" sx={{ scrollMarginTop: 100 }}>
        <ErmScorecard />
      </Box>

      {/* Pillars */}
      <Box sx={{ px: { xs: 2, sm: 3, md: 4 }, py: { xs: 8, md: 12 }, background: colors.white }}>
        <Box sx={{ maxWidth: 1100, mx: 'auto' }}>
          <ScrollReveal>
            <SectionHeader
              eyebrow="Index Pillars"
              title="Six pillars of ERM efficiency"
              highlightWords={['pillars']}
              subtitle="Each pillar contributes to your overall ERM Efficiency Index score—highlighting strengths and areas for targeted improvement."
            />
          </ScrollReveal>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: 'repeat(3, 1fr)' },
              gap: 2,
            }}
          >
            {INDEX_PILLARS.map((pillar) => (
              <Box
                key={pillar.title}
                sx={{
                  p: 3,
                  border: `1px solid ${colors.borderLight}`,
                  borderTop: `2px solid ${colors.navy}`,
                }}
              >
                <Text variant="eyebrow" sx={{ color: colors.slate, mb: 1, display: 'block' }}>
                  {pillar.number} · Sample {pillar.score}
                </Text>
                <Text variant="h4" color="primary" sx={{ mb: 1 }}>
                  {pillar.title}
                </Text>
                <Text variant="bodySm" color="secondary">
                  {pillar.description}
                </Text>
              </Box>
            ))}
          </Box>
        </Box>
      </Box>

      {/* Methodology */}
      <Box sx={{ px: { xs: 2, sm: 3, md: 4 }, py: { xs: 8, md: 12 }, background: colors.offWhite }}>
        <Box sx={{ maxWidth: 900, mx: 'auto' }}>
          <ScrollReveal>
            <SectionHeader
              eyebrow="Methodology"
              title="How the index works"
              highlightWords={['works']}
            />
          </ScrollReveal>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
            {METHODOLOGY_STEPS.map((step, i) => (
              <ScrollReveal key={step.number} delay={i * 0.05}>
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', sm: '80px 1fr' },
                    gap: 2,
                    py: 3,
                    borderTop: `1px solid ${colors.borderLight}`,
                  }}
                >
                  <Text variant="stat" color="primary">
                    {step.number}
                  </Text>
                  <Box>
                    <Text variant="h4" color="primary" sx={{ mb: 1 }}>
                      {step.title}
                    </Text>
                    <Text variant="body" color="secondary">
                      {step.description}
                    </Text>
                  </Box>
                </Box>
              </ScrollReveal>
            ))}
          </Box>
        </Box>
      </Box>

      {/* Benefits */}
      <Box sx={{ px: { xs: 2, sm: 3, md: 4 }, py: { xs: 8, md: 12 }, background: gradients.sectionDark }}>
        <Box sx={{ maxWidth: 800, mx: 'auto' }}>
          <ScrollReveal>
            <SectionHeader
              eyebrow="Why It Matters"
              title="Clarity modern governance demands."
              highlightWords={['Clarity']}
              subtitle="Boards and risk committees need more than binders. The ERM Efficiency Index delivers measurable insight."
              light
            />
          </ScrollReveal>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {BENEFITS.map((item) => (
              <Box
                key={item}
                sx={{
                  pl: 2.5,
                  borderLeft: `2px solid ${colors.silver}`,
                  py: 0.5,
                }}
              >
                <Text variant="body" color="muted">
                  {item}
                </Text>
              </Box>
            ))}
          </Box>
        </Box>
      </Box>

      <CtaSection
        title="Ready to score your ERM maturity?"
        subtitle="Request a briefing on the ERM Efficiency Index and how it connects to AERA™."
        primaryLabel="Contact ALEgION"
        primaryPath="/contact"
        secondaryLabel="Explore AERA™"
        secondaryPath="/framework"
      />
      <Footer />
    </Box>
  );
};

export default ErmIndex;
